import { ChangeDetectorRef, Component, Input, OnChanges, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CriticalDatesService } from '../../../service/entity-services/critical-dates.service';

/**
 * SANDBOX-ONLY reminder test tool, shown inside the Important Date dialog.
 *
 * Built for Ray (2026-10-06) to test the reminder date without waiting for the
 * one nightly run (11:00 UTC ≈ 7 AM ET). Three things:
 *   1. a plain-English line: will this date's reminder send, when, to whom — or why not;
 *   2. "Send test reminder now" — the exact mail the nightly run would send,
 *      marked [SANDBOX TEST], to internal staff only, changing nothing on the date;
 *   3. "Pretend today is…" — run the real nightly selection for sandbox as of a day.
 *
 * Rendered only when environment.reminderTestTools === true, which only
 * environment.sandbox-ng.ts sets. The backend enforces sandbox on its own (no org
 * parameter, sandbox-pool login required); this flag just keeps it off-screen.
 *
 * It checks the SAVED date, not unsaved edits in the form — the dialog closes on
 * save, so the panel says so rather than pretending otherwise.
 */
@Component({
  selector: 'app-reminder-test-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatProgressSpinnerModule],
  template: `
    <div class="rt-panel">
      <div class="rt-strip">SANDBOX TEST TOOL — reminder date</div>

      <div class="rt-section">
        <div class="rt-label">Based on the last saved version of this date:</div>
        @if (statusLoading) {
          <mat-spinner diameter="20"></mat-spinner>
        } @else if (status?.error) {
          <p class="rt-bad">{{ status.error }}</p>
        } @else if (status) {
          <p [class.rt-good]="status.state !== 'will-not-send'" [class.rt-bad]="status.state === 'will-not-send'">
            {{ status.summary }}
          </p>
          @if (status.reasons?.length > (status.state === 'will-not-send' ? 1 : 0)) {
            <ul class="rt-reasons">
              @for (r of status.reasons; track r) { <li>{{ r }}</li> }
            </ul>
          }
        }
        <button mat-stroked-button type="button" (click)="refreshStatus()" [disabled]="statusLoading">Check again</button>
      </div>

      <div class="rt-section">
        <button mat-raised-button color="primary" type="button" (click)="sendNow()" [disabled]="sending">
          Send test reminder now
        </button>
        <span class="rt-hint">Goes to staff addresses only. Does not use up the real reminder.</span>
        @if (sending) { <mat-spinner diameter="20"></mat-spinner> }
        @if (sendResult) {
          <div class="rt-result">
            @for (l of describeSend(sendResult); track $index) { <div [class]="l.kind">{{ l.text }}</div> }
          </div>
        }
      </div>

      <div class="rt-section">
        <div class="rt-label">Pretend today is…</div>
        <div class="rt-row">
          <input type="date" [value]="simDate" (change)="simDate = $any($event.target).value" />
          <button mat-stroked-button type="button" (click)="simulate(false)" [disabled]="simulating || !simDate">
            Show what would send
          </button>
          <button mat-stroked-button type="button" (click)="simulate(true)" [disabled]="simulating || !simDate">
            Send those as tests
          </button>
          @if (simulating) { <mat-spinner diameter="20"></mat-spinner> }
        </div>
        @if (simResult?.error) {
          <p class="rt-bad">{{ simResult.error }}</p>
        } @else if (simResult) {
          <p class="rt-label">
            On the morning of {{ simResult.simulatedDate }}, the reminder run would send
            <b>{{ simResult.wouldSend.length }}</b> sandbox reminder(s):
          </p>
          <ul class="rt-reasons">
            @for (d of simResult.wouldSend; track d.criticalDateId) {
              <li [class.rt-this]="d.criticalDateId === criticalDateId">
                {{ d.name }} — to {{ d.recipients.join(', ') || 'nobody' }}
                @if (d.criticalDateId === criticalDateId) { <b>(this date)</b> }
              </li>
            }
          </ul>
          @if (thisDateNotSending(simResult); as other) {
            <p class="rt-bad">This date would not send that day: {{ other.why.join(' ') }}</p>
          }
          @if (simResult.sends) {
            @for (s of simResult.sends; track s.criticalDateId) {
              <div class="rt-result">
                <b>{{ s.criticalDateId }}:</b>
                @for (l of describeSend(s); track $index) { <div [class]="l.kind">{{ l.text }}</div> }
              </div>
            }
          }
          <p class="rt-hint">{{ simResult.note }}</p>
        }
      </div>
    </div>
  `,
  styles: [`
    .rt-panel { border: 2px dashed #e0b100; border-radius: 6px; margin: 16px 0; background: #fffdf3; }
    .rt-strip { background: #fff3cd; color: #6b5200; font-weight: 600; font-size: 12px; letter-spacing: .04em;
                padding: 6px 12px; border-bottom: 1px solid #e0b100; }
    .rt-section { padding: 10px 12px; border-bottom: 1px solid #f3e7b8; display: flex; flex-direction: column;
                  align-items: flex-start; gap: 6px; }
    .rt-section:last-child { border-bottom: 0; }
    .rt-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
    .rt-label { font-size: 12px; color: #555; margin: 0; }
    .rt-hint { font-size: 12px; color: #777; }
    .rt-good { color: #1b5e20; margin: 0; font-weight: 500; }
    .rt-bad { color: #b71c1c; margin: 0; font-weight: 500; }
    .rt-reasons { margin: 0; padding-left: 20px; font-size: 13px; }
    .rt-this { font-weight: 600; }
    .rt-result { font-size: 13px; }
    input[type=date] { padding: 6px; font-size: 14px; }
  `]
})
export class ReminderTestPanelComponent implements OnChanges {
  @Input() criticalDateId = '';

  private criticalDatesService = inject(CriticalDatesService);

  /**
   * The app is ZONELESS (main.ts -> provideZonelessChangeDetection), so assigning a
   * plain field inside an HTTP subscribe does NOT notify Angular — the view keeps
   * rendering the pre-request state until some unrelated event happens to trigger
   * change detection. Every one of this panel's three actions is exactly that shape,
   * so without these notifies the spinners spin forever and no result ever appears.
   * Same fix, and same reason, as CreateEntity.cdrZoneless and ViewEntity.
   *
   * (Signals would notify on their own and are the newer idiom here; plain fields
   * plus markForCheck is what the surrounding code in this area already does, so it
   * stays consistent. If this panel grows, convert the whole thing to signals rather
   * than adding a fourth hand-written notify.)
   */
  private cdrZoneless = inject(ChangeDetectorRef);

  status: any = null;
  statusLoading = false;
  sending = false;
  sendResult: any = null;
  /**
   * Defaults to TODAY IN THE VIEWER'S OWN TIMEZONE, built from local parts rather
   * than toISOString(). toISOString() renders the UTC day, so for a US tester after
   * 5pm Pacific it pre-filled TOMORROW — on a panel whose entire job is answering
   * "what goes out on a given day". Same class of bug as the invoice DATES OF
   * SERVICE being a day early; see shared/utils/format-date-cell.ts.
   */
  simDate = (() => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  })();
  simulating = false;
  simResult: any = null;

  ngOnChanges(): void {
    if (this.criticalDateId) this.refreshStatus();
  }

  refreshStatus(): void {
    this.statusLoading = true;
    this.criticalDatesService.getReminderTestStatus(this.criticalDateId).subscribe(res => {
      this.status = res;
      this.statusLoading = false;
      this.cdrZoneless.markForCheck();
    });
  }

  sendNow(): void {
    this.sending = true;
    this.sendResult = null;
    this.criticalDatesService.sendTestReminder(this.criticalDateId).subscribe(res => {
      this.sendResult = res;
      this.sending = false;
      this.cdrZoneless.markForCheck();
    });
  }

  simulate(send: boolean): void {
    if (send && !confirm(`Send every sandbox reminder that would go out on ${this.simDate} as a test email to staff?`)) return;
    this.simulating = true;
    this.simResult = null;
    this.criticalDatesService.simulateReminderDay(this.simDate, send).subscribe(res => {
      this.simResult = res;
      this.simulating = false;
      this.cdrZoneless.markForCheck();
    });
  }

  thisDateNotSending(sim: any): any {
    return (sim?.wouldNotSend || []).find((d: any) => d.criticalDateId === this.criticalDateId) || null;
  }

  /** A send result as short lines for the template (Angular escapes them). */
  describeSend(r: any): { text: string; kind: string }[] {
    if (!r) return [];
    const lines: { text: string; kind: string }[] = [];
    if (r.error) lines.push({ text: r.error, kind: 'rt-bad' });
    if (r.sentTo?.length) {
      lines.push({ text: `Sent to ${r.sentTo.join(', ')}${r.withIcs ? ' (with calendar invite)' : ''}.`, kind: 'rt-good' });
    } else if (!r.error) {
      lines.push({ text: 'Nothing was sent.', kind: 'rt-bad' });
    }
    for (const s of r.skipped || []) lines.push({ text: `Skipped ${s.email} — ${s.reason}.`, kind: 'rt-hint' });
    for (const f of r.failed || []) lines.push({ text: `Failed ${f.email} — ${f.reason}.`, kind: 'rt-bad' });
    return lines;
  }
}
